import React, { useState } from "react";
import { Table, Tabs, Tab } from "react-bootstrap";
import { format } from "date-fns";
import {
    GrowthReportResponseTypeV4,
    PeriodType
} from "../../../../services/cm-growth-report/response/GrowthReportResponseV4";
import { formatPrice } from "../../../../shared/utils/formatPrice";

interface GrowthReportTableV4Props {
    data: GrowthReportResponseTypeV4;
}

// Helper function untuk memformat label periode agar mudah dibaca
const formatPeriodLabel = (period: PeriodType) => {
    // Menambahkan penyesuaian zona waktu untuk menghindari masalah tanggal off-by-one
    const startDate = new Date(period.period_info.start + 'T00:00:00');
    const endDate = new Date(period.period_info.end + 'T00:00:00');

    const start = format(startDate, "MMM yyyy");
    const end = format(endDate, "MMM yyyy");

    return start === end ? start : `${start} - ${end}`;
};


const GrowthReportTableV4: React.FC<GrowthReportTableV4Props> = ({ data }) => {
    // Cek jika tidak ada data atau data kosong
    if (!data || data.length === 0 || !data[0].comparisons || data[0].comparisons.length === 0) {
        return <p className="text-center mt-4">No comparison data available for the selected filters.</p>;
    }

    // Ambil daftar perbandingan dari data properti pertama (strukturnya sama untuk semua)
    const comparisons = data[0].comparisons;

    // State untuk mengelola tab yang sedang aktif, default ke perbandingan pertama
    const [activeTabKey, setActiveTabKey] = useState(`${comparisons[0].from_period}-${comparisons[0].to_period}`);

    // Temukan data perbandingan yang sesuai dengan tab aktif
    const activeComparisonTemplate = comparisons.find(c => `${c.from_period}-${c.to_period}` === activeTabKey);
    if (!activeComparisonTemplate) return <p>Error: Could not find active comparison.</p>;

    const fromPeriodIndex = activeComparisonTemplate.from_period;
    const toPeriodIndex = activeComparisonTemplate.to_period;

    // Ambil info periode dari data properti pertama untuk label header
    const fromPeriodInfo = data[0].periods.find(p => p.period_index === fromPeriodIndex);
    const toPeriodInfo = data[0].periods.find(p => p.period_index === toPeriodIndex);

    // Calculate totals for the active comparison
    const totals = data.reduce((acc, property) => {
        const fromPeriod = property.periods.find(p => p.period_index === fromPeriodIndex);
        const toPeriod = property.periods.find(p => p.period_index === toPeriodIndex);
        acc.fromNights += fromPeriod?.room_night_sold ?? 0;
        acc.toNights += toPeriod?.room_night_sold ?? 0;
        acc.fromRevenue += fromPeriod?.revenue ?? 0;
        acc.toRevenue += toPeriod?.revenue ?? 0;
        return acc;
    }, { fromNights: 0, toNights: 0, fromRevenue: 0, toRevenue: 0 });

    const totalNightGrowth = totals.fromNights > 0
        ? ((totals.toNights - totals.fromNights) / totals.fromNights) * 100
        : 0;
    const totalRevenueGrowth = totals.fromRevenue > 0
        ? ((totals.toRevenue - totals.fromRevenue) / totals.fromRevenue) * 100
        : 0;

    return (
        <div className="mt-4">
            <Tabs
                id="growth-report-tabs"
                activeKey={activeTabKey}
                onSelect={(k) => setActiveTabKey(k || "")}
                className="mb-3"
            >
                {/* Buat Tab secara dinamis berdasarkan data perbandingan yang tersedia */}
                {comparisons.map(comp => (
                    <Tab
                        key={`${comp.from_period}-${comp.to_period}`}
                        eventKey={`${comp.from_period}-${comp.to_period}`}
                        title={`Compare ${comp.from_period + 1} vs ${comp.to_period + 1}`}
                    />
                ))}
            </Tabs>

            {/* Render Tabel sesuai dengan Tab yang aktif */}
            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Property Name</th>
                            <th>Room Nights Sold ({fromPeriodInfo ? formatPeriodLabel(fromPeriodInfo) : ''})</th>
                            <th>Room Nights Sold ({toPeriodInfo ? formatPeriodLabel(toPeriodInfo) : ''})</th>
                            <th>Growth (Nights %)</th>
                            <th>Revenue ({fromPeriodInfo ? formatPeriodLabel(fromPeriodInfo) : ''})</th>
                            <th>Revenue ({toPeriodInfo ? formatPeriodLabel(toPeriodInfo) : ''})</th>
                            <th>Growth (Revenue %)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((property) => {
                            // Temukan data spesifik untuk setiap properti berdasarkan tab aktif
                            const fromPeriodData = property.periods.find(p => p.period_index === fromPeriodIndex);
                            const toPeriodData = property.periods.find(p => p.period_index === toPeriodIndex);
                            const comparisonData = property.comparisons.find(c => c.from_period === fromPeriodIndex && c.to_period === toPeriodIndex);

                            // Tambahkan pewarnaan pada nilai pertumbuhan
                            const rnsColor = (comparisonData?.room_night_growth ?? 0) < 0 ? "text-danger" : "text-success";
                            const revColor = (comparisonData?.revenue_growth ?? 0) < 0 ? "text-danger" : "text-success";

                            return (
                                <tr key={property.property_id}>
                                    <td>{property.property_name}</td>
                                    <td>{fromPeriodData?.room_night_sold ?? 'N/A'}</td>
                                    <td>{toPeriodData?.room_night_sold ?? 'N/A'}</td>
                                    <td className={rnsColor}>
                                        {comparisonData ? `${comparisonData.room_night_growth.toFixed(2)}%` : 'N/A'}
                                    </td>
                                    <td>{fromPeriodData ? formatPrice(fromPeriodData.revenue) : 'N/A'}</td>
                                    <td>{toPeriodData ? formatPrice(toPeriodData.revenue) : 'N/A'}</td>
                                    <td className={revColor}>
                                        {comparisonData ? `${comparisonData.revenue_growth.toFixed(2)}%` : 'N/A'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr className="table-light fw-bold">
                            <td>Total</td>
                            <td>{totals.fromNights}</td>
                            <td>{totals.toNights}</td>
                            <td className={totalNightGrowth < 0 ? "text-danger" : "text-success"}>
                                {totalNightGrowth.toFixed(2)}%
                            </td>
                            <td>{formatPrice(totals.fromRevenue)}</td>
                            <td>{formatPrice(totals.toRevenue)}</td>
                            <td className={totalRevenueGrowth < 0 ? "text-danger" : "text-success"}>
                                {totalRevenueGrowth.toFixed(2)}%
                            </td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default GrowthReportTableV4;